import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { META_DIMENSIONS_ITEMS } from "./QueryBuilder/QueryBuilderQueries";
import Loader from "components/Loader/Loader";

import { AppContext } from "contexts/AppContextProvider";
import { DEFAULT_FILTER } from "./PageContainer";
import Heading from "./Elements/Heading";
import Downshift from "downshift";
import clsx from "clsx";
import { BASEURL, HEADERS } from "./Utils/Constants";

const PageFilterContainer = ({ dimension, pageSettings }) => {
  const tenantID = parseInt(localStorage.defaultTenantID);
  const { currentProject } = useContext(AppContext).state;
  const {
    dispatch,
    state: { pageSettings: pageSettingsState },
  } = pageSettings;

  // Calculate date range: from 10 years ago to today
  const today = new Date();
  const tenYearsAgo = new Date();
  tenYearsAgo.setFullYear(today.getFullYear() - 10);

  const formatDate = (date) => {
    return date.toISOString().split("T")[0]; // YYYY-MM-DD format
  };

  const fromDate = formatDate(tenYearsAgo);
  const toDate = formatDate(today);

  const dimensionName = dimension.name;

  const renderFilters = (dimensions) => {
    let sorted_dimensions = [...(dimensions || [])].sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    sorted_dimensions.unshift(DEFAULT_FILTER);

    const selectedItems = (pageSettingsState.boardFilters || []).filter(
      (d) => d.dimensionId === dimension.id
    );

    const updatePageSettings = (obj) => {
      dispatch({
        type: "PAGE-SET-SETTINGS",
        payload: obj,
      });
    };

    const onChangeFilter = (selected_item, event, data) => {
      // Remove focus
      document.getElementById(event.id + "-input").blur();

      const elementSelected = {
        ...data.find(({ name }) => name === selected_item),
        dimensionId: dimension.id,
      };

      const allFilters = (pageSettingsState.boardFilters || []).filter(
        (d) =>
          !(
            d.id === elementSelected.id &&
            d.dimensionId === elementSelected.dimensionId
          )
      );

      allFilters.push(elementSelected);

      updatePageSettings({
        boardFilters: [...allFilters],
      });

      window.analytics.track("Filter", {
        type: "BoardFilter",
        elementSelected,
        project: currentProject.project,
        page: currentProject.page,
      });
    };

    const onStateChangeFilter = (change, e) => {
      const type = change.type;
      const input = document.getElementById(e.id + "-input");

      if (type === "__autocomplete_item_mouseenter__") {
        input.value = "";
      } else if (type === "__autocomplete_mouseup__") {
        input.value = pageSettingsState.filter_name;
      } else if (type === "__autocomplete_click_item__") {
        input.blur();
      }
    };

    const removeFilter = (item) => {
      const allFilters = (pageSettingsState.boardFilters || []).filter(
        (d) => !(d.id === item.id && d.name === item.name)
      );

      updatePageSettings({
        boardFilters: [...allFilters],
      });

      window.analytics.track("Filter", {
        type: "RemoveBoardFilter",
        item,
        project: currentProject.project,
        page: currentProject.page,
      });
    };

    return (
      <div className="col filters">
        <Heading size={6} key={"heading"}>
          <small>{dimensionName}</small>
        </Heading>
        <div className="d-flex filter-multi-select">
          {selectedItems.map((filter) => (
            <div key={filter.id} className="filter-select-input-multivalue">
              <div className="filter-select-input-tag-value">
                {filter.friendlyName}
              </div>
              <div
                className="filter-select-input-tag-close"
                onClick={() => removeFilter(filter)}
              ></div>
            </div>
          ))}
          {sorted_dimensions ? (
            <Downshift
              id={dimension.id.toString()}
              selectedItem={DEFAULT_FILTER.name}
              onChange={(selection, event) =>
                onChangeFilter(
                  selection,
                  event,
                  sorted_dimensions,
                  dimension.id
                )
              }
              onStateChange={(changes, stateAndHelpers, e) =>
                onStateChangeFilter(changes, stateAndHelpers, e)
              }
            >
              {({
                getInputProps,
                getItemProps,
                isOpen,
                toggleMenu,
                inputValue,
                highlightedIndex,
                selectedItem,
              }) => (
                <div style={{ position: "relative" }}>
                  <input
                    className="filters__input"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    id="downshift-input-0"
                    {...getInputProps({
                      placeholder: "Search For",
                      onClick(event) {
                        return (event.currentTarget.value = "");
                      },
                      onFocus() {
                        toggleMenu();
                      },
                    })}
                  />

                  {isOpen ? (
                    <div className="filters-wrapper">
                      <div className="filters-menu">
                        {sorted_dimensions
                          .filter((i) => {
                            if (inputValue === selectedItem) {
                              return i;
                            } else {
                              return i.name
                                .toLowerCase()
                                .includes(inputValue.toLowerCase());
                            }
                          })
                          .map((item, index, arr) => {
                            let itemClass = clsx("filters-menu-item", {
                              "filters-menu-item--active":
                                highlightedIndex === index,
                              "filters-menu-item--selected":
                                selectedItem === item.name,
                            });

                            return (
                              <div
                                {...getItemProps({
                                  key: item + index,
                                  index,
                                  item: item.name || "",
                                  className: itemClass,
                                })}
                              >
                                {item.friendlyName || ""}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </Downshift>
          ) : null}
        </div>
      </div>
    );
  };

  const { data, isError, isLoading } = useQuery({
    queryKey: ["pageFilterContainer", { dimensionId: dimension.id }],
    queryFn: () =>
      fetch(BASEURL + "/graphql", {
        method: "POST",
        headers: HEADERS(window.localStorage.token),
        body: JSON.stringify({
          query: META_DIMENSIONS_ITEMS.loc?.source?.body,
          variables: {
            dimensionId: dimension.id,
            tenantID,
            from: fromDate,
            to: toDate,
            // only applies this filter for Lee
            ...(currentProject.projectObj.id === "147" && dimension.id === 742
              ? { metricIds: [829] }
              : null),
            ...(currentProject.projectObj.id === "147" && dimension.id === 637
              ? { metricIds: [759] }
              : null),
            ...(currentProject.projectObj.id === "147" && dimension.id === 722
              ? { metricIds: [1128] }
              : null),
            // only applies this filter for Gotham
            ...(currentProject.projectObj.id === "165" && dimension.id === 400
              ? { metricIds: [759] }
              : null),
            project_id: currentProject.projectObj.id,
          },
        }),
      })
        .then((response) => response.json())
        .then((result) => result.data),
    refetchOnWindowFocus: false,
  });

  if (isError) {
    return (
      <div className="col-2">
        <small>Error loading filters</small>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="col-2">
        <Loader minHeight={100} size="circle-scale--sm  " />
      </div>
    );
  }

  return (
    <div className="pl-2 mb-3 filters-wrapper mb-md-0">
      <div className="pl-1 mt-2 mb-3 w-100 mb-md-1">
        {renderFilters(data.meta)}
      </div>
    </div>
  );
};

export default PageFilterContainer;
